import { ensureFreshToken } from './ensureFreshToken';
import { useConnectCredentialsStore } from '../../stores/session/connectCredentialsStore';
import { useSessionStore } from '../../stores/session/sessionStore';
import { useTenantStore, getCiu } from '../../stores/tenantStore';
import { oidcUserManager } from '../../services/oidcService';

// Deduplicates concurrent refresh calls: if multiple requests arrive with an
// expired token at the same time, they all await the same promise instead of
// each firing a separate AcceoConnect SSO round-trip.
let _refreshInFlight: Promise<unknown> | null = null;

/**
 * Builds the per-request tenant headers (tenant code, AcceoConnect token,
 * SaaS session token, CIU).
 *
 * Desktop sessions (sessionKind === 'desktop' with no acceoConnectToken) send only
 * X-Acceo-Ciu — no X-Connect-Token or X-Acceo-Session-Token, because desktop
 * Acomba does not use AcceoConnect. The app token travels as Authorization (set by apiClient).
 *
 * AcombaGO / browser sessions attach AcceoConnect headers and refresh the token on demand.
 * If the AcceoConnect token is already expired on entry, it is refreshed before the headers
 * are assembled — covering the case where the proactive refresh scheduler missed its window.
 */
export async function buildTenantHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  const tenantCode = useTenantStore.getState().tenantCode;
  if (tenantCode) headers['X-Tenant-Code'] = tenantCode;

  const sessionKind = useSessionStore.getState().sessionKind;

  // Desktop sessions have no AcceoConnect token — omit Connect headers entirely.
  if (sessionKind === 'desktop') {
    const ciu = getCiu();
    if (ciu !== null) headers['x-acceo-ciu'] = ciu;
    return headers;
  }

  // Portal sessions: the AcceoConnect token is held server-side and is never sent from the
  // browser — the backend sources it from the Connect session. AcombaGO/browser desktop sessions
  // still attach it here (and refresh it on demand). Both flows now carry an app refresh token,
  // so the session kind — not the token's presence — tells them apart.
  const isPortalSession = sessionKind === 'portal';
  if (!isPortalSession) {
    let { acceoConnectToken, isExpired, saasSessionToken } = useConnectCredentialsStore.getState();
    if (acceoConnectToken && isExpired()) {
      if (!_refreshInFlight) {
        _refreshInFlight = ensureFreshToken()
          .catch((e: unknown) => {
            console.error('[buildTenantHeaders] token refresh failed:', e);
            throw e;
          })
          .finally(() => { _refreshInFlight = null; });
      }
      try {
        await _refreshInFlight;
        ({ acceoConnectToken, isExpired, saasSessionToken } = useConnectCredentialsStore.getState());
      } catch {
        // Error already logged; omit connect-token headers — backend will return 401.
      }
    }

    if (acceoConnectToken && !isExpired()) {
      headers['X-Connect-Token'] = acceoConnectToken;
      if (saasSessionToken) headers['X-Acceo-Session-Token'] = saasSessionToken;
    }
  }

  // Forward the Identity Server JWT for the AcceoConnect SSO file hop: the state-of-account two-hop
  // into saasapi matches the request bearer against the IS JWT stored when the SaaS session token was
  // minted. Since the API bearer is now our app token, the IS JWT must travel in its own header —
  // portalIsToken for portal sessions, the live OIDC token for AcombaGO/browser desktop.
  const ssoBearer = isPortalSession
    ? useConnectCredentialsStore.getState().portalIsToken
    : (await oidcUserManager.getUser())?.access_token ?? null;
  if (ssoBearer) headers['X-Sso-Bearer'] = `Bearer ${ssoBearer}`;

  const ciu = getCiu();
  if (ciu !== null) headers['x-acceo-ciu'] = ciu;

  return headers;
}
